// PBP Guinée — Application principale
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { useAppSelector } from './store';
import MainLayout from './components/layout/MainLayout';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy loading des pages pour performance
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const PatrimoinePage = lazy(() => import('./pages/patrimoine/PatrimoinePage'));
const BienDetailPage = lazy(() => import('./pages/patrimoine/BienDetailPage'));
const BienFormPage = lazy(() => import('./pages/patrimoine/BienFormPage'));
const CartographiePage = lazy(() => import('./pages/cartographie/CartographiePage'));
const OccupationsPage = lazy(() => import('./pages/occupations/OccupationsPage'));
const BauxPage = lazy(() => import('./pages/baux/BauxPage'));
const MaintenancePage = lazy(() => import('./pages/maintenance/MaintenancePage'));
const JuridiquePage = lazy(() => import('./pages/juridique/JuridiquePage'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const RecensementPage = lazy(() => import('./pages/recensement/RecensementPage'));
const ReportingPage = lazy(() => import('./pages/reporting/ReportingPage'));
const UtilisateursPage = lazy(() => import('./pages/admin/UtilisateursPage'));
const ProfilPage = lazy(() => import('./pages/auth/ProfilPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Pas de retry pour les erreurs client (4xx)
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

// Guard routes authentifiées
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
        />

        <Route
          path="/"
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="patrimoine" element={<PatrimoinePage />} />
          <Route path="patrimoine/nouveau" element={<BienFormPage />} />
          <Route path="patrimoine/:id" element={<BienDetailPage />} />
          <Route path="patrimoine/:id/modifier" element={<BienFormPage />} />
          <Route path="cartographie" element={<CartographiePage />} />
          <Route path="occupations" element={<OccupationsPage />} />
          <Route path="baux" element={<BauxPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="juridique" element={<JuridiquePage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="recensement" element={<RecensementPage />} />
          <Route path="reporting" element={<ReportingPage />} />
          <Route path="admin/utilisateurs" element={<UtilisateursPage />} />
          <Route path="profil" element={<ProfilPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1e293b',
                color: '#f8fafc',
                border: '1px solid #334155',
              },
              success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}
